import React, { useState } from 'react'
import {
    Dialog, DialogContent, Box, Typography, IconButton, TextField,
    FormControl, FormHelperText, Select, MenuItem, Button, CircularProgress
} from '@mui/material'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useTheme } from '@emotion/react'
import api from '../../../service/api'
import { toast } from 'react-toastify'
import { Icons } from '../../../utils/utilities'

const AddFeaturePermissionDialog = ( { dialogState, setState, refreshData } ) => {
    const [adding, setAdding] = useState( false )
    const { palette, border } = useTheme()

    const initialValues = {
        feature_name: '',
        feature_key: '',
        default_permission: true
    }

    const validationSchema = Yup.object().shape( {
        feature_name: Yup.string().required( "Feature name is required!" ),
        feature_key: Yup.string().required( "Feature key is required!" ),
        default_permission: Yup.boolean().required( "Default permission state is required!" )
    } )

    // Automatically format the key: lowercase and replace special chars/spaces with "_"
    const handleNameChange = ( e, setFieldValue ) => {
        const nameValue = e.target.value
        setFieldValue( 'feature_name', nameValue )

        if ( nameValue && nameValue.trim() !== "" ) {
            const generatedKey = nameValue
                .trim()
                .replace( /[^0-9a-zA-Z]/g, "_" )
                .toLowerCase()
            setFieldValue( 'feature_key', generatedKey )
        } else {
            setFieldValue( 'feature_key', "" )
        }
    }

    const handleAddFeature = async ( values, { resetForm } ) => {
        setAdding( true )
        try {
            // This calls the initializeFeature logic we discussed earlier
            await api.featurePermissions.addNewFeature( values )
            refreshData()
            toast( `Feature "${values.feature_name}" initialized successfully` )
            resetForm()
            setState( false )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while adding feature" )
        } finally {
            setAdding( false )
        }
    }

    return (
        <Dialog open={dialogState} slotProps={{
            paper: { sx: { width: "90vw", maxWidth: "500px" } }
        }}>
            {/* Header */}
            <Box padding="10px 20px" sx={{ borderBottom: border[1], display: 'flex', alignItems: "center", justifyContent: 'space-between' }} >
                <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Add New Feature Permission</Typography>
                <IconButton onClick={() => setState( false )} size="small">
                    {Icons.default.CloseIcon}
                </IconButton>
            </Box>

            <DialogContent className='custom-scrollbar'>
                <Formik onSubmit={handleAddFeature} initialValues={initialValues} validationSchema={validationSchema}>
                    {( { setFieldValue } ) => (
                        <Form>
                            {/* Feature Name */}
                            <FormControl fullWidth margin="normal">
                                <Typography component="label" display="block" htmlFor="feature-name" gutterBottom variant="subtitle2" color={palette.form.labelColor}>
                                    Feature Name *
                                </Typography>
                                <Field
                                    autoComplete='off'
                                    placeholder="Eg: Payroll Export"
                                    size="small"
                                    sx={{ fontSize: "14px" }}
                                    as={TextField}
                                    name='feature_name'
                                    id="feature-name"
                                    onChange={( e ) => handleNameChange( e, setFieldValue )}
                                />
                                <FormHelperText error><ErrorMessage name='feature_name' /></FormHelperText>
                            </FormControl>

                            {/* Feature Key (Auto-generated) */}
                            <FormControl fullWidth margin="normal">
                                <Typography component="label" display="block" htmlFor="feature-key" gutterBottom variant="subtitle2" color={palette.form.labelColor}>
                                    Feature Key (Unique ID) *
                                </Typography>
                                <Field
                                    placeholder="auto_generated_key"
                                    size="small"
                                    sx={{ fontSize: "14px" }}
                                    as={TextField}
                                    name='feature_key'
                                    id="feature-key"
                                    InputProps={{ readOnly: true }} // Keep it read-only to maintain strict formatting
                                />
                                <FormHelperText error><ErrorMessage name='feature_key' /></FormHelperText>
                            </FormControl>

                            {/* Default Permission State */}
                            <FormControl fullWidth margin="normal">
                                <Typography component="label" display="block" htmlFor="default-permission" gutterBottom variant="subtitle2" color={palette.form.labelColor}>
                                    Default Permission for Legacy Institutes *
                                </Typography>
                                <Field size="small" sx={{ fontSize: "14px" }} as={Select} name='default_permission' id="default-permission">
                                    <MenuItem sx={{ fontSize: "14px" }} value={true}>Allowed (Default)</MenuItem>
                                    <MenuItem sx={{ fontSize: "14px" }} value={false}>Restricted</MenuItem>
                                </Field>
                                <FormHelperText sx={{ fontSize: '11px' }}>
                                    Determines if existing institutes get access immediately.
                                </FormHelperText>
                                <FormHelperText error><ErrorMessage name='default_permission' /></FormHelperText>
                            </FormControl>

                            {/* Action Buttons */}
                            <Box marginTop="30px" display="flex" gap="10px" alignItems="center">
                                <Button
                                    startIcon={adding ? <CircularProgress size={14} /> : Icons.default.AddIcon}
                                    disabled={adding}
                                    type="submit"
                                    disableElevation
                                    sx={{ textTransform: "capitalize" }}
                                    variant='contained'
                                >
                                    {adding ? "Initializing..." : "Create Feature"}
                                </Button>
                                <Button
                                    onClick={() => setState( false )}
                                    color="error"
                                    sx={{ textTransform: "capitalize" }}
                                    variant="outlined"
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    )
}

export default AddFeaturePermissionDialog